import type { Session as SupabaseSession } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from './supabase.js';

/**
 * Authentication state.
 *
 * Deliberately thin: it answers "is someone signed in, and who" and nothing
 * else. Authorisation, roles, permissions, which organisation, comes from
 * `GET /v1/session`, so that the server stays the single source of truth for
 * what a user may do. A client that decides its own permissions is a client
 * that can be edited in a browser console.
 */

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly userId: string | null;
  readonly email: string | null;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SupabaseSession | null>(null);
  // Starts as `loading`, never as `anonymous`. Rendering a signed-in user the
  // login screen for one frame while the stored session is read is jarring and
  // makes protected routes flicker.
  const [status, setStatus] = useState<AuthStatus>('loading');

  useEffect(() => {
    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setStatus(data.session === null ? 'anonymous' : 'authenticated');
    });

    // Covers token refresh, sign-out in another tab, and expiry.
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setStatus(nextSession === null ? 'anonymous' : 'authenticated');
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      userId: session?.user.id ?? null,
      email: session?.user.email ?? null,
      async signIn(email: string, password: string) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error !== null) {
          // Supabase deliberately returns the same message for a wrong password
          // and an unknown address, which prevents account enumeration. Passing
          // it through preserves that property.
          throw new Error(error.message);
        }
      },
      async signOut() {
        await supabase.auth.signOut();
      },
    }),
    [status, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAuth must be used within an <AuthProvider>.');
  }
  return value;
}
